import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
  memo,
  useTransition,
} from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import "../pages.css";

import {
  MButton,
  MHeader,
  LoadingContext,
  M_StateActions,
  NavigateContext,
} from "src/M_Components";

import { BufferService } from "src/M_Components/M_Utils/buffer.service";
import { isFailurePayload, apiMessage } from "src/components/utils/apiResponse";
import DynamicForm from "src/components/Forms/DynamicForm";
import EsriGenericMap from "src/components/Maps/EsriGenericMap";

/* --------------------------------------------------------------------------------------------------------------------
 * toCamel
 *
 * Converts a PascalCase key into a lower camelCase key.
 *
 * Purpose in this file:
 * - DynamicForm schema keys can be PascalCase.
 * - Backend DTO properties are typically lower camelCase.
 * - This helper provides a consistent conversion for payload building.
 * ------------------------------------------------------------------------------------------------------------------ */
const toCamel = (s) => (s ? s.charAt(0).toLowerCase() + s.slice(1) : s);

/* --------------------------------------------------------------------------------------------------------------------
 * isBoolean
 *
 * Returns true when a schema option describes a boolean field.
 *
 * Purpose in this file:
 * - Schema metadata may represent boolean types using different strings.
 * - Payload building should always coerce these to strict true/false values.
 * ------------------------------------------------------------------------------------------------------------------ */
const isBoolean = (opt) => opt?.Type === "boolean" || opt?.Type === "bool";

/* --------------------------------------------------------------------------------------------------------------------
 * deriveId
 *
 * Determines the best available Id for the current Country entity.
 *
 * Purpose in this file:
 * - The Id can arrive from different sources (table line, fetched details, snapshot).
 * - A single precedence order prevents divergence across code paths.
 *
 * Priority:
 * 1) sessionLine.Id (explicit selection from a list/table)
 * 2) countryInfo.Id (hydrated from backend)
 * 3) snapshot countryId (persisted per-tab state)
 * 4) local state Id (fallback)
 * ------------------------------------------------------------------------------------------------------------------ */
const deriveId = (line, info, current, snap) =>
  (line && line.Id) != null
    ? line.Id
    : (info && info.Id) != null
      ? info.Id
      : snap != null
        ? snap
        : current != null
          ? current
          : null;

/* --------------------------------------------------------------------------------------------------------------------
 * shallowEqualPatch
 *
 * Returns true when a patch does not change any values of a base object.
 *
 * Purpose in this file:
 * - Map clicks can emit multiple near-identical events.
 * - Avoiding redundant state writes reduces rerenders and improves responsiveness.
 * ------------------------------------------------------------------------------------------------------------------ */
const shallowEqualPatch = (base, patch) => {
  if (!patch || typeof patch !== "object") return true;
  for (const k of Object.keys(patch)) {
    if ((base || {})[k] !== patch[k]) return false;
  }
  return true;
};

/* --------------------------------------------------------------------------------------------------------------------
 * nextPaint / afterPaint
 *
 * Small synchronization primitives for "visual completion".
 *
 * Purpose in this file:
 * - Some ArcGIS operations resolve before the browser paints the final WebGL frame.
 * - Waiting for one or two animation frames allows the highlight and extent to become visible reliably.
 *
 * Notes:
 * - This is aligned with the browser render loop (not duration-based).
 * - Frame count is configurable to balance smoothness and responsiveness.
 * ------------------------------------------------------------------------------------------------------------------ */
const nextPaint = () => new Promise((resolve) => requestAnimationFrame(resolve));

const afterPaint = async (frames = 1) => {
  const count = Number.isFinite(frames) ? Math.max(1, Math.trunc(frames)) : 1;
  for (let i = 0; i < count; i += 1) {
    // eslint-disable-next-line no-await-in-loop
    await nextPaint();
  }
};

/* --------------------------------------------------------------------------------------------------------------------
 * isAbortError
 *
 * Detects AbortError-like failures across different runtimes.
 *
 * Purpose in this file:
 * - ArcGIS interactions are frequently cancellable (select, hitTest, gestures, reconfigurations).
 * - Cancellation is expected control-flow and should not be treated as a runtime error.
 * ------------------------------------------------------------------------------------------------------------------ */
const isAbortError = (e) => {
  if (!e) return false;
  if (e?.name === "AbortError") return true;
  if (e?.message === "Aborted") return true;
  const msg = String(e?.message || "");
  return msg.toLowerCase().includes("aborted");
};

/* --------------------------------------------------------------------------------------------------------------------
 * getBoundMapAction
 *
 * Returns a "this-bound" mapAction function.
 *
 * Purpose in this file:
 * - Some imperative map APIs rely on `this` for internal state.
 * - Binding keeps the function context stable when the method is passed around.
 * - Both direct instances and `.current` refs are supported.
 * ------------------------------------------------------------------------------------------------------------------ */
const getBoundMapAction = (componentOrRef) => {
  const c = componentOrRef?.current ? componentOrRef.current : componentOrRef;
  if (c && typeof c.mapAction === "function") return c.mapAction.bind(c);
  return null;
};

/**
 * CRUD screen for the Country entity.
 *
 * Responsibilities:
 * - Load DynamicForm schema options and entity details depending on operation mode
 * - Persist and restore per-tab state snapshots
 * - Render a DynamicForm and an ArcGIS map for selecting/highlighting countries
 * - Synchronize map selections with form values, including ContinentId inference
 * - Update the tab label according to operation and current entity name
 *
 * Map behavior:
 * - Uses selectByAttribute on the "World_Countries" layer
 * - Prefers the layer OID field (FID) to make selection deterministic
 * - Falls back to ISO_CC, then to COUNTRY name when needed
 * - Uses highlight + optional zoom-to-extent for view mode
 * - Avoids redundant selection calls using a lightweight de-duplication key
 *
 * Rendering behavior:
 * - Uses a ResizeObserver to ensure the WebGL canvas is resized when the container size changes
 * - Uses a responsive height (clamp) to keep the map visible on small screens
 */
export default function CountryConfigCrud({ handleTabClose, toastMessages }) {
  /**
   * Redux dispatch for shared state updates and per-tab snapshot persistence.
   */
  const dispatch = useDispatch();

  /**
   * Shared application state used for tab/session snapshots and transient globals.
   */
  const M_State = useSelector((state) => state.StateKeyValue);

  /**
   * Translation helpers and current language code.
   */
  const { t, i18n } = useTranslation();

  /**
   * Loading context used to show/hide a loading indicator and to access the base API URL.
   */
  const ctx = useContext(LoadingContext);

  /**
   * Navigation context used to rename the active tab.
   */
  const { renameTab } = useContext(NavigateContext);

  /**
   * Selected tab id retrieved from shared state.
   * Used as the per-tab snapshot key.
   */
  const selectedTab = useSelector((s) =>
    s.StateKeyValue.find((o) => o.key === "selectedTab")?.value
  );

  /**
   * React transition signal used to reflect pending UI commits in loading logic.
   */
  const [isPendingUi, startUiTransition] = useTransition();

  /**
   * A small tick used to re-evaluate derived values when only refs change.
   */
  const [selectionTick, setSelectionTick] = useState(0);

  /**
   * Forces derived memo/effects to recompute when ref-only state changes.
   */
  const bumpSelectionTick = useCallback(() => {
    setSelectionTick((x) => x + 1);
  }, []);

  /**
   * Global operation and line may be injected by upstream navigation logic.
   * These are consumed once and then removed from shared state.
   */
  const globalOperation = M_State.find((obj) => obj.key === "operation")?.value;
  const globalLine = M_State.find((obj) => obj.key === "CountryLine")?.value;

  /**
   * Per-tab session identifier (typically equals selectedTab).
   */
  const [id, setId] = useState(
    M_State.find((obj) => obj.key === "selectedTab")?.value
  );

  /**
   * Current operation mode: create/edit/update/view.
   */
  const [operation, setOperation] = useState(globalOperation ?? "Error");

  /**
   * Session line data containing entity identifiers and contextual values.
   */
  const [sessionLine, setSessionLine] = useState(globalLine ?? {});

  /**
   * Current entity data bound to the DynamicForm.
   */
  const [countryInfo, setCountryInfo] = useState({});

  /**
   * Flag used to prevent some effects from running during initial hydration.
   */
  const [isFirst, setIsFirst] = useState(true);

  /**
   * Current entity identifier used for update operations.
   * It is derived from the most reliable source whenever sessionLine/countryInfo changes.
   */
  const [countryId, setCountryId] = useState(sessionLine?.Id);

  /* =====================================================================================================================
   * DynamicForm bits
   * =================================================================================================================== */

  /**
   * DynamicForm schema describing fields and constraints.
   */
  const [schema, setSchema] = useState([]);

  /**
   * Imperative DynamicForm API reference.
   * Used to submit and to reset values without re-rendering the form component itself.
   */
  const formRef = useRef(null);

  /**
   * Stores per-foreign-key seed query text so the FK selection components can be opened with a pre-filled search term.
   * This improves UX during map selection because the map provides human labels rather than ids.
   */
  const [fkInitialQueries, setFkInitialQueries] = useState({});

  /* =====================================================================================================================
   * Ref mirrors
   *
   * Purpose:
   * - Keeps async flows and callbacks aligned with latest values.
   * - Avoids stale closure usage across delayed operations.
   * =================================================================================================================== */

  const idRef = useRef(id);
  const operationRef = useRef(operation);
  const countryIdRef = useRef(countryId);
  const sessionLineRef = useRef(sessionLine);
  const countryInfoRef = useRef(countryInfo);

  useEffect(() => {
    idRef.current = id;
  }, [id]);

  useEffect(() => {
    operationRef.current = operation;
  }, [operation]);

  useEffect(() => {
    countryIdRef.current = countryId;
  }, [countryId]);

  useEffect(() => {
    sessionLineRef.current = sessionLine;
  }, [sessionLine]);

  useEffect(() => {
    countryInfoRef.current = countryInfo;
  }, [countryInfo]);

  /**
   * Token used to invalidate pending async operations when the tab context changes.
   * Incremented when `id` changes.
   */
  const tabTokenRef = useRef(0);
  useEffect(() => {
    tabTokenRef.current += 1;
  }, [id]);

  /* =====================================================================================================================
   * Backend services
   * =================================================================================================================== */

  const svcCountries = useMemo(
    () => new BufferService("Countries", ctx.DEFAULTURL),
    [ctx.DEFAULTURL]
  );

  const svcCreate = useMemo(
    () => new BufferService("Countries/create", ctx.DEFAULTURL),
    [ctx.DEFAULTURL]
  );

  const svcUpdate = useMemo(
    () => new BufferService("Countries/update", ctx.DEFAULTURL),
    [ctx.DEFAULTURL]
  );

  const svcContinents = useMemo(
    () => new BufferService("Continents", ctx.DEFAULTURL),
    [ctx.DEFAULTURL]
  );

  /* =====================================================================================================================
   * Operation normalization
   * =================================================================================================================== */

  /**
   * Normalizes the operation into "create" | "edit" | "view".
   * This keeps downstream logic stable even when upstream values vary.
   */
  const normalizedOperation = useMemo(() => {
    const raw = String(operation || "").trim();
    const low = raw.toLowerCase();
    if (low === "create") return "create";
    if (low === "edit") return "edit";
    if (low === "update") return "edit";
    if (low === "view") return "view";
    if (raw === "Create") return "create";
    if (raw === "Edit") return "edit";
    if (raw === "View") return "view";
    return low || "view";
  }, [operation]);

  /**
   * View mode disables editing and disables map selection handlers.
   */
  const isViewMode = normalizedOperation === "view";

  /**
   * Map wrapper mode expected by EsriGenericMap.
   * The wrapper uses "update" rather than "edit".
   */
  const mapMode = useMemo(() => {
    if (normalizedOperation === "create") return "create";
    if (normalizedOperation === "edit") return "update";
    return "view";
  }, [normalizedOperation]);

  /**
   * Locale passed to the map wrapper.
   */
  const mapLocale = useMemo(() => {
    const lng = String(i18n.language || "pt").toLowerCase();
    if (lng.startsWith("pt")) return "pt-PT";
    if (lng.startsWith("es")) return "es-ES";
    return "en-US";
  }, [i18n.language]);

  /* =====================================================================================================================
   * Tab label / snapshots
   * =================================================================================================================== */

  /**
   * Updates the tab label whenever the operation or entity name changes.
   */
  useEffect(() => {
    if (!selectedTab || !operation) return;

    const key =
      normalizedOperation === "create"
        ? "LabelNewCountry"
        : normalizedOperation === "edit"
          ? "LabelEditCountry"
          : "LabelViewCountry";

    const name = sessionLine?.Name ?? countryInfo?.Name ?? "";
    const base = t(key);
    const label =
      normalizedOperation === "create" || !name ? base : `${base} "${name}"`;

    renameTab(String(selectedTab), label);
  }, [
    selectedTab,
    operation,
    normalizedOperation,
    sessionLine?.Name,
    countryInfo?.Name,
    renameTab,
    t,
  ]);

  /**
   * Initial mount:
   * - Restores snapshot (if present)
   * - Consumes global operation/line
   * - Bootstraps data loading
   */
  useEffect(() => {
    const sessionForThisTab = M_State.find((o) => o.key === id)?.value || [];
    const pick = (k) => sessionForThisTab.find((x) => x.key === k)?.value;

    const snapInfo = pick("countryInfo");
    const snapIF = pick("isFirst");
    const snapOP = pick("operation");
    const snapSL = pick("sessionLine");
    const snapSchema = pick("Dynamic Form.schema");
    const snapId = pick("countryId");
    const snapFk = pick("Dynamic Form.fkInitialQueries");

    if (snapInfo) setCountryInfo(snapInfo);
    if (snapSchema) setSchema(snapSchema);
    if (snapFk) setFkInitialQueries(snapFk);

    if (snapIF !== undefined) setIsFirst(snapIF);
    else if (isFirst) setIsFirst(false);

    const seedOperation = globalOperation !== undefined ? globalOperation : snapOP;
    const seedLine = globalLine !== undefined ? globalLine : snapSL;

    const effectiveOperation = seedOperation ?? operation;
    const effectiveLine = seedLine ?? sessionLine;
    const effectiveId = deriveId(effectiveLine, snapInfo ?? countryInfo, countryId, snapId);

    setOperation(effectiveOperation);
    setSessionLine(effectiveLine);
    setCountryId(effectiveId);

    if (globalOperation !== undefined)
      dispatch(M_StateActions.removeM_State({ key: "operation" }));
    if (globalLine !== undefined)
      dispatch(M_StateActions.removeM_State({ key: "CountryLine" }));

    bootstrap(effectiveOperation, effectiveLine);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Persists a per-tab snapshot with a small debounce to reduce Redux write frequency.
   */
  const saveSnapshotTimer = useRef(null);
  useEffect(() => {
    if (!id) return;

    const sessionData = [
      { key: "countryInfo", value: countryInfo },
      { key: "isFirst", value: isFirst },
      { key: "operation", value: operation },
      { key: "sessionLine", value: sessionLine },
      { key: "Dynamic Form.schema", value: schema },
      { key: "Dynamic Form.fkInitialQueries", value: fkInitialQueries },
      { key: "countryId", value: countryId },
    ];

    if (saveSnapshotTimer.current) clearTimeout(saveSnapshotTimer.current);
    saveSnapshotTimer.current = setTimeout(() => {
      dispatch(M_StateActions.addM_State({ key: id, value: sessionData }));
    }, 80);

    return () => {
      if (saveSnapshotTimer.current) clearTimeout(saveSnapshotTimer.current);
    };
  }, [id, countryInfo, isFirst, operation, sessionLine, schema, fkInitialQueries, countryId, dispatch]);

  /**
   * Detects selected tab changes and restores state for the new tab id.
   */
  useEffect(() => {
    const newId = M_State.find((obj) => obj.key === "selectedTab")?.value;
    if (newId !== id) {
      setId(newId);
      getSessionStorageData(newId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [M_State]);

  /**
   * Recomputes the tab label and reloads options when the language changes.
   * Options must be reloaded so NormalizedName and enum labels are localized.
   */
  useEffect(() => {
    if (!operation || isFirst) return;

    fetchOptions({ skipDetails: true });

    const key =
      normalizedOperation === "create"
        ? "LabelNewCountry"
        : normalizedOperation === "edit"
          ? "LabelEditCountry"
          : "LabelViewCountry";

    const name = sessionLine?.Name ?? countryInfo?.Name ?? "";
    const base = t(key);
    const label =
      normalizedOperation === "create" || !name ? base : `${base} "${name}"`;

    renameTab(String(selectedTab), label);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  /**
   * Keeps countryId aligned with the most reliable Id source.
   */
  useEffect(() => {
    setCountryId((prev) => deriveId(sessionLine, countryInfo, prev, null));
  }, [sessionLine, countryInfo]);

  /* =====================================================================================================================
   * Data loading
   * =================================================================================================================== */

  /**
   * Restores a snapshot for a given tab id from shared state, or triggers bootstrap.
   */
  const getSessionStorageData = (sessionID) => {
    ctx.setIsLoad(true);

    const M_StateData = M_State.find((obj) => obj.key === sessionID)?.value;
    if (M_StateData) {
      const map = {};
      M_StateData.forEach((o) => {
        map[o.key] = o.value;
      });

      const restoredInfo = map["countryInfo"] || {};
      const restoredLine = map["sessionLine"];

      setCountryInfo(restoredInfo);
      setIsFirst(map["isFirst"] ?? false);
      setOperation(map["operation"]);
      setSessionLine(restoredLine);

      if (map["Dynamic Form.schema"]) setSchema(map["Dynamic Form.schema"]);
      if (map["Dynamic Form.fkInitialQueries"]) setFkInitialQueries(map["Dynamic Form.fkInitialQueries"]);

      setCountryId(deriveId(restoredLine, restoredInfo, countryIdRef.current, map["countryId"]));

      ctx.setIsLoad(false);
      return;
    }

    bootstrap(operationRef.current, sessionLineRef.current);
  };

  /**
   * Loads schema options and (when applicable) entity details.
   * Uses a tab token to ignore late completions when user switches tabs.
   */
  const bootstrap = async (op, line) => {
    const token = tabTokenRef.current;

    ctx.setIsLoad(true);
    try {
      await fetchOptions(token);

      const opLow = String(op || "").toLowerCase();
      const idToGet = line?.Id || sessionLineRef.current?.Id;

      if (opLow !== "create" && idToGet) {
        await fetchDetails(idToGet, token);
      }
    } finally {
      ctx.setIsLoad(false);
    }
  };

  /**
   * Fetches the DynamicForm schema options from the backend.
   * Guards against late completions using the provided token.
   */
  const fetchOptions = async (tokenFromCaller) => {
    const myToken = tokenFromCaller ?? tabTokenRef.current;

    try {
      const res = await svcCountries.options();

      if (isFailurePayload(res)) {
        toastMessages("error", "", apiMessage(res, t("ErrorLoadingData")));
        return;
      }

      if (myToken !== tabTokenRef.current) return;

      setSchema(Array.isArray(res) ? res : []);
    } catch (err) {
      toastMessages("error", "", apiMessage(err, t("ErrorLoadingData")));
    }
  };

  /**
   * Fetches entity details by id.
   * Guards against late completions using the provided token.
   */
  const fetchDetails = async (idToGet, tokenFromCaller) => {
    const myToken = tokenFromCaller ?? tabTokenRef.current;

    try {
      const response = await svcCountries.getId(idToGet);

      if (isFailurePayload(response)) {
        toastMessages("error", "", apiMessage(response, t("ErrorLoadingData")));
        return;
      }

      if (myToken !== tabTokenRef.current) return;

      const hydrated = response ? { ...response } : {};

      setCountryInfo(hydrated);
      setCountryId(hydrated?.Id);

      setSessionLine((prev) =>
        prev && prev.Id ? prev : { ...prev, Id: hydrated?.Id, Name: hydrated?.Name }
      );
    } catch (err) {
      toastMessages("error", "", apiMessage(err, t("ErrorLoadingData")));
    }
  };

  /**
   * Refreshes schema and (if applicable) details for the current tab context.
   */
  const getRefresh = (line) => {
    const effectiveLine = line || sessionLineRef.current;
    const opLow = String(operationRef.current || "").toLowerCase();

    if (opLow !== "create" && !effectiveLine?.Id) {
      ctx.setIsLoad(false);
      return;
    }

    ctx.setIsLoad(true);

    Promise.all([
      fetchOptions(),
      opLow !== "create" ? fetchDetails(effectiveLine?.Id) : Promise.resolve(),
    ]).finally(() => ctx.setIsLoad(false));
  };

  /* =====================================================================================================================
   * Form handlers
   * =================================================================================================================== */

  /**
   * Merges DynamicForm field changes into component local state.
   * This keeps map selection and tab labels in sync with the form.
   */
  const handleFormValuesChange = useCallback((patch, meta) => {
    if (meta?.cause === "fk-query" && meta?.name) {
      setFkInitialQueries((prev) => ({
        ...prev,
        [meta.name]: meta.query ?? "",
      }));
      return;
    }

    if (!patch) return;
    setCountryInfo((prev) => ({ ...prev, ...patch }));
  }, []);

  /**
   * Builds the API payload from form values based on schema metadata.
   */
  const buildPayloadFromValues = (values) => {
    const opts = Array.isArray(schema) ? schema : [];
    const entries = [];

    for (const opt of opts) {
      if (!opt) continue;

      const formKey = opt.IsForeignKey ? opt.Header || opt.Name : opt.Name;
      if (!formKey) continue;

      const payloadKey = toCamel(opt.Header || opt.Name);
      let v = values?.[formKey];

      if (isBoolean(opt)) {
        v = !!v;
      } else if (opt.IsForeignKey) {
        v = v == null || v === "" ? null : Number(v);
      }

      if (v !== undefined) entries.push([payloadKey, v]);
    }

    return Object.fromEntries(entries);
  };

  /**
   * Submits the current form values to the backend API (create or update).
   */
  const onSubmitDynamic = useCallback(
    async (values) => {
      ctx.setIsLoad(true);
      try {
        const payload = buildPayloadFromValues(values);

        if (!payload.code || !payload.name || !payload.continentId || !payload.currencyId) {
          toastMessages("error", "", t("MandatoryFields"));
          return;
        }

        const isCreate = normalizedOperation === "create";
        const cid =
          countryIdRef.current ??
          deriveId(sessionLineRef.current, countryInfoRef.current, null, null);

        if (!isCreate && !cid) {
          toastMessages("error", "", t("ErrorSaving"));
          return;
        }

        const res = isCreate
          ? await svcCreate.post(payload)
          : await svcUpdate.update(cid, payload);

        if (isFailurePayload(res)) {
          toastMessages("error", "", apiMessage(res, t("ErrorSaving")));
          return;
        }

        toastMessages("success", "", isCreate ? t("CountryCreated") : t("CountryUpdated"));
        handleTabClose(idRef.current);
      } catch (err) {
        toastMessages("error", "", apiMessage(err, t("ErrorSaving")));
      } finally {
        ctx.setIsLoad(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ctx, t, toastMessages, svcCreate, svcUpdate, schema, normalizedOperation, handleTabClose]
  );

  /**
   * Triggers DynamicForm submission through the imperative formRef API.
   */
  const triggerFormSubmit = () => {
    formRef.current?.submit?.();
  };

  /* =====================================================================================================================
   * Map configuration
   * =================================================================================================================== */

  /**
   * Layer definition for the Countries feature service.
   *
   * Notes:
   * - The title must match the layer name used by mapAction("selectByAttribute", { layer: "Countries", ... }).
   * - The World_Countries layer uses FID as the OID field.
   */
  const countriesLayer = useMemo(
    () => [
      {
        title: "Countries",
        type: "serviceLayer",
        url: "https://services.arcgis.com/P3ePLMYs2RVChkJx/ArcGIS/rest/services/World_Countries/FeatureServer/0",
        symbol: {
          type: "simple-fill",
          color: [0, 0, 0, 0],
          outline: { type: "simple-line", color: [0, 0, 0, 255], width: 1 },
        },
      },
    ],
    []
  );

  /**
   * Map widgets configuration (kept stable via useMemo).
   */
  const mapWidgets = useMemo(() => ({ search: { position: "top-right" } }), []);

  /**
   * Map initial center.
   */
  const mapCenter = useMemo(() => [-9.14, 38.72], []);

  /**
   * Initial zoom and map constraints.
   *
   * Notes:
   * - Setting minZoom equal to initial zoom prevents zooming out below the initial view.
   * - This keeps the map from becoming visually "smaller" than its container due to aggressive zoom-out.
   */
  const initialZoom = 2;

  const mapConstraints = useMemo(
    () => ({
      minZoom: initialZoom,
    }),
    []
  );

  /**
   * List of layers that can emit feature info on click.
   */
  const countryFeatureInfoLayers = useMemo(() => ["Countries"], []);

  /**
   * Reference to the map component API instance (assigned on onComponentReady).
   */
  const mapComponentRef = useRef(null);

  /**
   * Reference to the map container DOM element.
   * Used for ResizeObserver-based reflow to keep WebGL canvas sized correctly.
   */
  const mapHostRef = useRef(null);

  /* =====================================================================================================================
   * Responsive resize
   * =================================================================================================================== */

  /**
   * Ensures the map canvas is resized whenever the container size changes.
   * This is critical when:
   * - The layout changes at responsive breakpoints
   * - The user switches tabs
   * - Side panels open/close
   */
  useEffect(() => {
    const host = mapHostRef.current;
    if (!host) return undefined;

    let rafId = 0;

    const scheduleResize = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const action = getBoundMapAction(mapComponentRef.current);
        if (!action) return;

        try {
          action("resize");
        } catch {
          /* noop */
        }
      });
    };

    scheduleResize();

    const ro = new ResizeObserver(() => {
      scheduleResize();
    });

    ro.observe(host);

    return () => {
      try {
        ro.disconnect();
      } catch {
        /* noop */
      }
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  /* =====================================================================================================================
   * Map selection helpers
   * =================================================================================================================== */

  /**
   * Builds the where clause for ArcGIS selectByAttribute.
   *
   * Priority:
   * - FID (deterministic and efficient)
   * - ISO_CC (stable and compact identifier)
   * - COUNTRY (exact match as fallback)
   */
  const buildWhereFromPick = useCallback((rawCountry, rawIso, rawFid) => {
    if (rawFid !== null && rawFid !== undefined && rawFid !== "") {
      const n = Number(rawFid);
      if (Number.isFinite(n)) return `FID = ${Math.trunc(n)}`;
    }

    const iso = String(rawIso || "").trim().toUpperCase();
    if (iso) {
      const safeIso = iso.replace(/'/g, "''");
      return `ISO_CC = '${safeIso}'`;
    }

    const name = String(rawCountry || "").trim();
    if (!name) return "1=0";

    const safe = name.replace(/'/g, "''");
    return `COUNTRY = '${safe}'`;
  }, []);

  /**
   * Small cache for continent name -> continent id lookups.
   *
   * Purpose in this file:
   * - Clicking multiple countries within the same continent is common.
   * - Caching reduces repeated backend calls and improves perceived responsiveness.
   */
  const continentIdCacheRef = useRef(new Map());

  /**
   * Attempts to resolve ContinentId based on continent display name.
   *
   * Notes:
   * - The filter expression is string-based and always quoted to avoid parsing differences.
   * - Returning null is treated as a valid outcome when the continent is not present in the database.
   */
  const resolveContinentIdByName = useCallback(
    async (continentName) => {
      const name = String(continentName || "").trim();
      if (!name) return null;

      const cacheKey = name.toLowerCase();
      if (continentIdCacheRef.current.has(cacheKey)) {
        return continentIdCacheRef.current.get(cacheKey);
      }

      const safe = name.replace(/'/g, "''");
      const expr = `Name eq '${safe}'`;
      const qs = `?Page=1&PageSize=25&Filter=${encodeURIComponent(expr)}`;

      const res = await svcContinents.filter(qs);
      if (isFailurePayload(res)) {
        continentIdCacheRef.current.set(cacheKey, null);
        return null;
      }

      const items = Array.isArray(res?.Items)
        ? res.Items
        : Array.isArray(res)
          ? res
          : [];

      if (!items.length) {
        continentIdCacheRef.current.set(cacheKey, null);
        return null;
      }

      const hit =
        items.find((x) => String(x?.Name || "").toLowerCase() === cacheKey) ?? items[0];

      const idNum = Number(hit?.Id);
      const id = Number.isFinite(idNum) && idNum > 0 ? idNum : null;

      continentIdCacheRef.current.set(cacheKey, id);
      return id;
    },
    [svcContinents]
  );

  /**
   * Stores cleanup callbacks returned from mapAction("selectByAttribute").
   * These callbacks remove highlight and filter state from the map.
   */
  const lastSelectionCleanupRef = useRef({
    removeFilter: null,
    removeHighlight: null,
  });

  /**
   * Token used to ignore out-of-order selection results.
   * Each new selection increments the token. Late results from older selections are discarded.
   */
  const selectionTokenRef = useRef(0);

  /**
   * De-duplicates selection requests that target the same feature.
   * This reduces redundant highlight work and keeps the map more responsive.
   */
  const lastSelectionKeyRef = useRef(null);

  /**
   * Clears the last stored selection state by invoking any stored cleanup callbacks.
   */
  const clearLastSelection = useCallback(() => {
    try {
      lastSelectionCleanupRef.current.removeHighlight?.();
    } catch {
      /* noop */
    }
    try {
      lastSelectionCleanupRef.current.removeFilter?.();
    } catch {
      /* noop */
    }
    lastSelectionCleanupRef.current = { removeFilter: null, removeHighlight: null };
  }, []);

  /* =====================================================================================================================
   * Selection loading scope
   *
   * The loader is derived from:
   * - selection workflow being active
   * - in-flight async work
   * - selection workflow marked complete
   * - highlight painted (afterPaint)
   * - React transition still pending
   * =================================================================================================================== */

  const selectLoadingActiveRef = useRef(false);
  const selectInflightRef = useRef(0);
  const selectCompletedRef = useRef(false);
  const selectHighlightPaintedRef = useRef(false);

  const beginSelectionLoading = useCallback(() => {
    selectLoadingActiveRef.current = true;
    selectInflightRef.current = 0;
    selectCompletedRef.current = false;
    selectHighlightPaintedRef.current = false;
    bumpSelectionTick();
    ctx.setIsLoad(true);
  }, [bumpSelectionTick, ctx]);

  const beginSelectionAsync = useCallback(() => {
    selectInflightRef.current += 1;
    bumpSelectionTick();
  }, [bumpSelectionTick]);

  const endSelectionAsync = useCallback(() => {
    selectInflightRef.current = Math.max(0, selectInflightRef.current - 1);
    bumpSelectionTick();
  }, [bumpSelectionTick]);

  const markSelectionCompleted = useCallback(() => {
    selectCompletedRef.current = true;
    bumpSelectionTick();
  }, [bumpSelectionTick]);

  const markSelectionHighlightPainted = useCallback(() => {
    selectHighlightPaintedRef.current = true;
    bumpSelectionTick();
  }, [bumpSelectionTick]);

  /**
   * Derived loader value computed from ref-only state plus React transition signal.
   * The selectionTick dependency forces recomputation when refs are updated.
   */
  const derivedSelectionLoading = useMemo(() => {
    if (!selectLoadingActiveRef.current) return false;

    const inflight = selectInflightRef.current > 0;
    const completed = selectCompletedRef.current;
    const painted = selectHighlightPaintedRef.current;

    return inflight || !completed || !painted || isPendingUi;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPendingUi, selectionTick]);

  /**
   * Synchronizes derived loading state to the shared loading context.
   * When selection loading finishes, scope flags are reset.
   */
  useEffect(() => {
    if (!selectLoadingActiveRef.current) return;

    ctx.setIsLoad(!!derivedSelectionLoading);

    if (!derivedSelectionLoading) {
      selectLoadingActiveRef.current = false;
      selectInflightRef.current = 0;
      selectCompletedRef.current = false;
      selectHighlightPaintedRef.current = false;
    }
  }, [ctx, derivedSelectionLoading]);

  /**
   * Applies a map highlight/filter based on the selected country and optionally zooms to its extent.
   *
   * Behavior:
   * - Uses mapAction("selectByAttribute") with highlightGeometry and optional zoomToExtent.
   * - Stores cleanup callbacks for subsequent clearing.
   * - Discards late results using selectionTokenRef.
   * - De-duplicates identical selections using lastSelectionKeyRef.
   */
  const selectCountryOnMap = useCallback(
    async (countryName, iso, fid, { zoomToExtent } = { zoomToExtent: false }) => {
      const action = getBoundMapAction(mapComponentRef.current);
      if (!action) return null;

      const where = buildWhereFromPick(countryName, iso, fid);
      if (!where || where === "1=0") return null;

      const selectionKey = `layer:Countries|where:${where}|zoom:${zoomToExtent ? "1" : "0"}`;
      if (lastSelectionKeyRef.current === selectionKey) return null;
      lastSelectionKeyRef.current = selectionKey;

      const myToken = ++selectionTokenRef.current;

      try {
        const res = await action("selectByAttribute", {
          layer: "Countries",
          where,
          filterGeometry: false,
          zoomToExtent: !!zoomToExtent,
          highlightGeometry: true,
          extentFactor: !!zoomToExtent ? 1.02 : 1.0,
        });

        const removeFilter =
          typeof res?.removeFilter === "function" ? res.removeFilter : null;
        const removeHighlight =
          typeof res?.removeHighlight === "function" ? res.removeHighlight : null;

        if (myToken !== selectionTokenRef.current) {
          try {
            removeHighlight?.();
          } catch {
            /* noop */
          }
          try {
            removeFilter?.();
          } catch {
            /* noop */
          }
          return null;
        }

        const count = Array.isArray(res?.features) ? res.features.length : 0;

        if (count > 0) {
          clearLastSelection();
          lastSelectionCleanupRef.current = { removeFilter, removeHighlight };
        } else {
          try {
            removeHighlight?.();
          } catch {
            /* noop */
          }
          try {
            removeFilter?.();
          } catch {
            /* noop */
          }
        }

        await afterPaint(2);
        return res;
      } catch (err) {
        if (isAbortError(err)) return null;
        // eslint-disable-next-line no-console
        console.error("[CountryConfigCrud] Map select/highlight failed:", err);
        return null;
      }
    },
    [buildWhereFromPick, clearLastSelection]
  );

  /**
   * Applies a patch to:
   * - local model state (countryInfo)
   * - DynamicForm values through its imperative reset API
   *
   * Behavior:
   * - Preserves dirty/touched/errors so user editing context is not lost.
   * - Uses a shallow no-op guard to avoid redundant resets.
   * - Wraps writes in startUiTransition so loader derivation can include the commit phase.
   */
  const applyPatchToForm = useCallback(
    (patch) => {
      if (!patch || typeof patch !== "object") return;

      if (shallowEqualPatch(countryInfoRef.current, patch)) return;

      startUiTransition(() => {
        setCountryInfo((prev) => ({ ...prev, ...patch }));
      });

      const api = formRef.current;
      if (api?.getValues && api?.reset) {
        const current = api.getValues() || {};
        const next = { ...current, ...patch };

        let changed = false;
        for (const k of Object.keys(patch)) {
          if (current?.[k] !== next?.[k]) {
            changed = true;
            break;
          }
        }
        if (!changed) return;

        startUiTransition(() => {
          api.reset(next, {
            keepDirty: true,
            keepTouched: true,
            keepErrors: true,
          });
        });
      }
    },
    [startUiTransition]
  );

  /* =====================================================================================================================
   * Map events
   * =================================================================================================================== */

  /**
   * Suppresses duplicate picks emitted in quick succession.
   * This can happen when the map returns near-duplicate hit results.
   */
  const lastPickedRef = useRef({ key: null, ts: 0 });

  /**
   * Triggered when a user clicks a country feature on the map (create/edit modes).
   *
   * Flow:
   * - Extract country attributes (name + ISO + FID + continent name)
   * - De-duplicate near-identical events
   * - Patch form values (Name + Code + EsriFid + ContinentId)
   * - Highlight selection on map without zoom
   */
  const handleMapFeatureInfo = useCallback(
    async (res) => {
      const first = Array.isArray(res) ? res[0] : res;
      if (isViewMode) return;
      if (!first) return;

      const attrs = first?.attributes || first?.feature?.attributes || {};

      const country =
        attrs.COUNTRY ??
        attrs.Country ??
        attrs.country ??
        attrs.NAME ??
        attrs.Name;

      const iso = attrs.ISO_CC ?? attrs.iso_cc ?? attrs.IsoCc ?? attrs.ISO;
      const fid = attrs.FID ?? attrs.fid ?? attrs.OBJECTID ?? attrs.objectid;
      const continent = attrs.CONTINENT ?? attrs.Continent ?? attrs.continent;

      const key =
        fid != null
          ? `fid:${String(fid)}`
          : iso
            ? `iso:${String(iso)}`
            : country
              ? `name:${String(country)}`
              : null;

      if (key) {
        const now = Date.now();
        if (lastPickedRef.current.key === key && now - lastPickedRef.current.ts < 250) return;
        lastPickedRef.current = { key, ts: now };
      }

      if (!country && !iso && fid == null) return;

      beginSelectionLoading();

      const patch = {};

      if (country) patch.Name = String(country);

      if (iso) patch.Code = String(iso);

      if (fid != null && fid !== "" && Number.isFinite(Number(fid))) {
        patch.EsriFid = Math.trunc(Number(fid));
      }

      const continentName = String(continent || "").trim();

      if (continentName) {
        startUiTransition(() => {
          setFkInitialQueries((prev) => ({
            ...prev,
            ContinentId: continentName,
          }));
        });

        beginSelectionAsync();
        try {
          const contId = await resolveContinentIdByName(continentName);
          patch.ContinentId = contId ?? null;
        } catch (e) {
          if (isAbortError(e)) return;
          patch.ContinentId = null;
        } finally {
          endSelectionAsync();
        }
      } else {
        startUiTransition(() => {
          setFkInitialQueries((prev) => ({ ...prev, ContinentId: "" }));
        });
        patch.ContinentId = null;
      }

      applyPatchToForm(patch);
      markSelectionCompleted();

      await selectCountryOnMap(country, iso, fid, { zoomToExtent: false });
      markSelectionHighlightPainted();
    },
    [
      isViewMode,
      beginSelectionLoading,
      beginSelectionAsync,
      endSelectionAsync,
      applyPatchToForm,
      markSelectionCompleted,
      selectCountryOnMap,
      markSelectionHighlightPainted,
      resolveContinentIdByName,
      startUiTransition,
    ]
  );

  /**
   * Retry mechanism for view-mode highlighting when the layer is not ready yet.
   */
  const highlightTimerRef = useRef(null);

  /**
   * Prevents repeated highlight execution when the view-mode target has not changed.
   */
  const lastViewHighlightKeyRef = useRef(null);

  /**
   * Highlights and zooms to the current country in view mode.
   *
   * Behavior:
   * - Prefers stored EsriFid when present because it is deterministic
   * - Then prefers Code (ISO) when present
   * - Falls back to name-based selection when ISO/FID are unavailable
   * - Attempts selectByAttribute with zoom-to-extent
   * - Retries once when the selection returns no features
   * - Falls back to highlight-only when zoom-to-extent does not yield results
   */
  const highlightCountryInView = useCallback(() => {
    if (!isViewMode) return;

    const rawName = String(countryInfoRef.current?.Name || "").trim();
    const rawCode = String(countryInfoRef.current?.Code || "").trim();
    const fidCandidate = countryInfoRef.current?.EsriFid;

    const isoCandidate = rawCode ? rawCode.toUpperCase() : "";
    const nameCandidate = rawName;

    if (fidCandidate == null && !isoCandidate && !nameCandidate) return;

    const where = buildWhereFromPick(nameCandidate, isoCandidate, fidCandidate);
    const key = `view:${where}`;

    if (lastViewHighlightKeyRef.current === key) return;
    lastViewHighlightKeyRef.current = key;

    if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);

    const doHighlightOnly = async () => {
      await selectCountryOnMap(nameCandidate, isoCandidate, fidCandidate, { zoomToExtent: false });
    };

    const doZoomAndHighlight = async () => {
      await selectCountryOnMap(nameCandidate, isoCandidate, fidCandidate, { zoomToExtent: true });
    };

    const action = getBoundMapAction(mapComponentRef.current);
    if (!action) {
      highlightTimerRef.current = setTimeout(() => {
        doZoomAndHighlight().catch(() => {
          doHighlightOnly();
        });
      }, 400);
      return;
    }

    doZoomAndHighlight()
      .then((res) => {
        const count = Array.isArray(res?.features) ? res.features.length : 0;
        if (count > 0) return;

        highlightTimerRef.current = setTimeout(() => {
          doZoomAndHighlight().catch(() => {
            highlightTimerRef.current = setTimeout(() => {
              doHighlightOnly();
            }, 250);
          });
        }, 400);
      })
      .catch((e) => {
        if (isAbortError(e)) return;

        highlightTimerRef.current = setTimeout(() => {
          doZoomAndHighlight().catch(() => {
            highlightTimerRef.current = setTimeout(() => {
              doHighlightOnly();
            }, 250);
          });
        }, 400);
      });
  }, [isViewMode, buildWhereFromPick, selectCountryOnMap]);

  /**
   * Clears any pending view-mode retry timer on unmount.
   */
  useEffect(() => {
    return () => {
      if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
    };
  }, []);

  /**
   * Runs view-mode highlighting only when the selection target changes.
   */
  useEffect(() => {
    if (!isViewMode) return;

    const rawName = String(countryInfo?.Name || "").trim();
    const rawCode = String(countryInfo?.Code || "").trim();
    const fidCandidate = countryInfo?.EsriFid;

    const isoCandidate = rawCode ? rawCode.toUpperCase() : "";
    const where = buildWhereFromPick(rawName, isoCandidate, fidCandidate);
    const key = `view:${where}`;

    if (!rawName && !isoCandidate && fidCandidate == null) return;
    if (lastViewHighlightKeyRef.current === key) return;

    highlightCountryInView();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isViewMode, countryInfo?.Name, countryInfo?.Code, countryInfo?.EsriFid]);

  /**
   * Receives the map component API instance when the map becomes ready.
   * Triggers initial highlight based on operation mode.
   */
  const handleMapReady = useCallback(
    (component) => {
      mapComponentRef.current = component;

      if (isViewMode) {
        highlightCountryInView();
        return;
      }

      const rawName = String(countryInfoRef.current?.Name || "").trim();
      const rawCode = String(countryInfoRef.current?.Code || "").trim();
      const fidCandidate = countryInfoRef.current?.EsriFid;

      const isoCandidate = rawCode ? rawCode.toUpperCase() : "";

      if (fidCandidate != null || rawName || isoCandidate) {
        selectCountryOnMap(rawName, isoCandidate, fidCandidate, { zoomToExtent: false });
      }
    },
    [isViewMode, highlightCountryInView, selectCountryOnMap]
  );

  /* =====================================================================================================================
   * UI keys / map panel
   * =================================================================================================================== */

  /**
   * Forces DynamicForm remount when operation or entity identity changes.
   */
  const dynamicFormKey = useMemo(() => {
    const entityId = deriveId(sessionLine, countryInfo, countryId, null) ?? "new";
    return `df-countries-${selectedTab}-${normalizedOperation}-${entityId}`;
  }, [selectedTab, normalizedOperation, sessionLine, countryInfo, countryId]);

  /**
   * Memoized map wrapper.
   *
   * Purpose:
   * - Insulates the WebGL subtree from frequent parent re-renders.
   * - Uses a responsive height on small screens via clamp().
   * - Avoids contain:size because WebGL canvases rely on accurate layout sizing.
   */
  const MapPanel = useMemo(() => {
    return memo(function MapPanelInner({
      mode,
      locale,
      widgets,
      layers,
      center,
      zoom,
      constraints,
      onComponentReady,
      featureInfoLayers,
      onFeatureInfo,
    }) {
      return (
        <div
          ref={mapHostRef}
          style={{
            height: "clamp(800px, 60vh, 800px)",
            border: "2px solid var(--color-red-dark)",
            borderRadius: 8,
            overflow: "hidden",
            position: "relative",
            boxSizing: "border-box",
            contain: "layout paint",
          }}
        >
          <EsriGenericMap
            mode={mode}
            locale={locale}
            widgets={widgets}
            layers={layers}
            center={center}
            zoom={zoom}
            constraints={constraints}
            onComponentReady={onComponentReady}
            featureInfoLayers={featureInfoLayers}
            onFeatureInfo={onFeatureInfo}
            onMapClick={undefined}
            configDebounceMs={180}
          />
        </div>
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* =====================================================================================================================
   * Render
   * =================================================================================================================== */

  return (
    <>
      <MHeader
        element={
          <>
            <MButton
              tooltip={t("RefreshTable")}
              icon={<i className="pi pi-refresh iconTable"></i>}
              onClick={() => getRefresh(sessionLine)}
              label={t("RefreshTable")}
            />
            {!isViewMode && (
              <MButton
                tooltip={t("Save")}
                icon={<i className="pi pi-save iconTable"></i>}
                onClick={triggerFormSubmit}
                label={t("Save")}
              />
            )}
          </>
        }
      />

      <div style={{ padding: "20px", background: "var(--color-white)" }}>
        <div className="firstDivContainer">
          <div className="country-config-layout">
            <div className="country-config-form">
              <DynamicForm
                key={dynamicFormKey}
                formRef={formRef}
                mode={normalizedOperation}
                schema={schema}
                data={countryInfo}
                onValuesChange={handleFormValuesChange}
                onSubmit={onSubmitDynamic}
                title={t("Country Information")}
                containerClassName="alignInputsUserForm"
                fkInitialQueries={fkInitialQueries}
                align="vertical"
              />
            </div>

            <div className="country-config-map">
              <MapPanel
                mode={mapMode}
                locale={mapLocale}
                widgets={mapWidgets}
                layers={countriesLayer}
                center={mapCenter}
                zoom={initialZoom}
                constraints={mapConstraints}
                onComponentReady={handleMapReady}
                featureInfoLayers={!isViewMode ? countryFeatureInfoLayers : undefined}
                onFeatureInfo={!isViewMode ? handleMapFeatureInfo : undefined}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

/**
 * Runtime prop type validation for CountryConfigCrud.
 */
CountryConfigCrud.propTypes = {
  handleTabClose: PropTypes.func,
  toastMessages: PropTypes.func,
};
